// PE-22 F — everything the weeks x days matrix writes. The matrix replaced the
// composer's staged D/W chip strips AND the spine's week stepper: a gesture
// writes NOW instead of arming a commit, so this is the write path and there
// is no "before" left to validate in.
//
// It composes the proven block algebra instead of touching sessions itself:
// updateBlock / applyBlock / removeBlock already reconcile slot ids, keep
// positions stable and sweep slots left with no active week
// (programBlocksApply.test.ts pins all three). Nothing new reaches the draft.
//
// The rectangle, and why a toggle can split a block: a ComposedBlock is
// members x weeks x days — `weeks` is block-wide, `placements` is per day —
// so ONE cell outside that rectangle is not expressible in one block. We scope
// every write to the toggled day, exactly like applyBlockEditScoped does for a
// day-scoped content edit, and let the next deriveBlocks split the divergent
// day into its own block. Copy-on-write, no stored block identity — the same
// mechanism that already runs when you edit one day of a cloned exercise.

import { type Draft } from "./programEdit.js";
import {
  hasActiveWeek,
  type Block,
  type ComposedBlock,
} from "./programBlocks.js";
import { applyBlock, removeBlock, updateBlock } from "./programBlocksExpand.js";
import { MIN_CYCLE_WEEKS } from "./programEditActions.js";

/** The block narrowed to a single day — the unit every write below acts on. */
const scopeToDay = (block: ComposedBlock, dayIdx: number): ComposedBlock => ({
  ...block,
  placements: block.placements.filter((p) => p.dayIdx === dayIdx),
});

/** A fresh placement for a day the block does not occupy yet. Empty slotIds
 *  mean "create a new slot" and -1 positions mean "append" — applyBlock's
 *  contract, same as a brand-new compose. */
const freshPlacement = (block: ComposedBlock, dayIdx: number) => ({
  dayIdx,
  slotIds: block.members.map(() => ""),
  positions: block.members.map(() => -1),
  group: null,
});

export const isPlaced = (
  block: ComposedBlock,
  dayIdx: number,
  week: number,
): boolean =>
  block.weeks.includes(week) &&
  block.placements.some((p) => p.dayIdx === dayIdx);

/** Turn the (week, day) cell on for this block. Widening an occupied day adds
 *  the week to THAT day only; a day the block does not occupy yet starts with
 *  just this week, never the block's whole span — the gesture places one cell,
 *  not a column. */
const place = (
  draft: Draft,
  block: ComposedBlock,
  dayIdx: number,
  week: number,
): Draft => {
  const onDay = scopeToDay(block, dayIdx);
  if (onDay.placements.length === 0) {
    const added: Block = {
      kind: "composed",
      members: block.members,
      weeks: [week],
      placements: [freshPlacement(block, dayIdx)],
    };
    return applyBlock(draft, added);
  }
  if (block.weeks.includes(week)) return draft;
  const widened: ComposedBlock = {
    ...onDay,
    weeks: [...onDay.weeks, week].sort((a, b) => a - b),
  };
  return updateBlock(draft, onDay, widened);
};

/** Turn the (week, day) cell off. Dropping the day's last active week removes
 *  the exercise from that day entirely — removeBlock's sweep, not a slot left
 *  behind with every week inactive. */
const unplace = (
  draft: Draft,
  block: ComposedBlock,
  dayIdx: number,
  week: number,
): Draft => {
  const onDay = scopeToDay(block, dayIdx);
  if (onDay.placements.length === 0) return draft;
  const weeks = onDay.weeks.filter((w) => w !== week);
  if (weeks.length === onDay.weeks.length) return draft;
  return weeks.length === 0
    ? removeBlock(draft, onDay)
    : updateBlock(draft, onDay, { ...onDay, weeks });
};

/** The matrix's hold (and the selected cell's switch, which must do the same
 *  thing — onLongPress never reaches VoiceOver on iOS). Returns the draft
 *  unchanged when the toggle would be a no-op, so a caller can compare by
 *  identity to know whether anything moved. */
export const togglePlacement = (
  draft: Draft,
  block: ComposedBlock,
  dayIdx: number,
  week: number,
): Draft =>
  isPlaced(block, dayIdx, week)
    ? unplace(draft, block, dayIdx, week)
    : place(draft, block, dayIdx, week);

// The − on a week row. setCycleWeeks only ever truncated the TAIL, so dropping
// a deload in the middle was not expressible: this splices the column out and
// lets the later weeks slide left, which is what a per-row − has to mean.
//
// Every per-week array moves together — cells, notesPerCell, notesRawPerCell
// and inactiveWeeks are index-parallel by contract, and a splice that missed
// one would silently shift a note onto the wrong week. Slots left with no
// active week are swept, exactly as the tail shrink does: dropping a week
// drops the sets that lived only there, which is what the confirm promises.
export const removeWeekAt = (draft: Draft, week: number): Draft => {
  if (draft.cycleWeeks <= MIN_CYCLE_WEEKS) return draft;
  if (week < 0 || week >= draft.cycleWeeks) return draft;
  const drop = <T,>(arr: T[]): T[] => arr.filter((_, i) => i !== week);
  return {
    ...draft,
    cycleWeeks: draft.cycleWeeks - 1,
    sessions: draft.sessions.map((s) => ({
      ...s,
      slots: s.slots
        .map((sl) => ({
          ...sl,
          inactiveWeeks: drop(sl.inactiveWeeks),
          setRows: sl.setRows.map((r) => ({
            ...r,
            cells: drop(r.cells),
            notesPerCell: drop(r.notesPerCell),
            ...(r.notesRawPerCell ? { notesRawPerCell: drop(r.notesRawPerCell) } : {}),
          })),
        }))
        .filter(hasActiveWeek),
    })),
  };
};
